import Service from '@ember/service';
import { inject as service } from '@ember/service';
import * as ParametricData from '../utils/parametric';
import { DataType as D, CardinalityType as C } from '../utils/parametric';
import type CdpService from '../services/cdp';
import type LoggingService from '../services/logger';
import type ModelService from '../services/model';
import type { OpAmpSpec } from '../services/model';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import { PSTData, PSTIndex, PSTParameterMapping } from '@ebiz/ember-aditools-device-table/utils/device-table-pst';

type SpiceDetails = {
  value: string;
};

type CfbDetails = {
  rIn: number;
  minRf: number;
  zbw: number;
};

type SpiceModelDetails = {
  compPin: boolean;
};

type OpAmpPSTParameterName =
  | 'partNumber'
  | 'gbw'
  | 'slewRate'
  | 'rOut'
  | 'supplyCurrent'
  | 'iNoise'
  | 'vNoise'
  | 'outHeadroomPlus'
  | 'outHeadroomMinus'
  | 'inHeadroomPlus'
  | 'inHeadroomMinus'
  | 'feedbackMethod'
  | 'architecture'
  | 'minSupplyVoltage'
  | 'maxSupplyVoltage'
  | 'numberChannels'
  | 'packageCode'
  | 'minStableGain'
  | 'iBias'
  | 'vos'
  | 'vintage'
  | 'statusText'
  | 'deviceStatus'
  | 'price'
  | 'tempRange'
  | 'cCm'
  | 'cDiff'
  | 'generic';

const PST_PARAMETERS: ParametricData.PstParameterSet<OpAmpPSTParameterName> = {
  '0': { id: '0', name: 'partNumber', data: D.t_none, value: C.c_none, optional: true },
  '4502': {
    id: '4502',
    name: 'gbw',
    data: D.t_float,
    value: C.c_single,
    optional: (part) => {
      return part.feedbackMethod === 'Current';
    },
  },
  '300': { id: '300', name: 'slewRate', data: D.t_float, value: C.c_single, optional: false },
  '4401': { id: '4401', name: 'rOut', data: D.t_float, value: C.c_single, optional: true },
  '4108': { id: '4108', name: 'supplyCurrent', data: D.t_float, value: C.c_single, optional: false },
  '4124': {
    id: '4124',
    name: 'iNoise',
    data: D.t_float,
    value: C.c_single,
    optional: false,
    default: (part) => {
      if (
        'iBias' in part &&
        !isNaN(Number(part.iBias)) &&
        parseFloat(part.iBias) < 80e-12 &&
        'architecture' in part &&
        part.architecture.length > 0 &&
        -1 === part.architecture.indexOf('Zero Drift')
      ) {
        return '1e-12';
      } else {
        return;
      }
    },
  },
  '4101': { id: '4101', name: 'vNoise', data: D.t_float, value: C.c_single, optional: false },
  '4099': { id: '4099', name: 'outHeadroomPlus', data: D.t_float, value: C.c_single, optional: false },
  '4100': { id: '4100', name: 'outHeadroomMinus', data: D.t_float, value: C.c_single, optional: false },
  '4084': { id: '4084', name: 'inHeadroomPlus', data: D.t_float, value: C.c_single, optional: false },
  '4085': { id: '4085', name: 'inHeadroomMinus', data: D.t_float, value: C.c_single, optional: false },
  '5034': { id: '5034', name: 'feedbackMethod', data: D.t_pulldown, value: C.c_single, optional: true },
  '5056': { id: '5056', name: 'architecture', data: D.t_pulldown, value: C.c_multi, optional: true },
  '2839': { id: '2839', name: 'minSupplyVoltage', data: D.t_float, value: C.c_single, optional: false },
  '2840': { id: '2840', name: 'maxSupplyVoltage', data: D.t_float, value: C.c_single, optional: false },
  '2687': { id: '2687', name: 'numberChannels', data: D.t_integer, value: C.c_single, optional: false },
  s4: { id: 's4', name: 'packageCode', data: D.t_pulldown, value: C.c_multi, optional: false },
  '4127': { id: '4127', name: 'minStableGain', data: D.t_float, value: C.c_single, optional: false },
  '4500': { id: '4500', name: 'iBias', data: D.t_float, value: C.c_single, optional: false },
  '4501': { id: '4501', name: 'vos', data: D.t_float, value: C.c_single, optional: false },
  '4455': { id: '4455', name: 'cCm', data: D.t_float, value: C.c_single, optional: false },
  '4117': { id: '4117', name: 'cDiff', data: D.t_float, value: C.c_single, optional: false },
  s1: { id: 's1', name: 'vintage', data: D.t_integer, value: C.c_single, optional: true },
  s7: { id: 's7', name: 'statusText', data: D.t_string, value: C.c_single, optional: true },
  s11: { id: 's11', name: 'deviceStatus', data: D.t_integer, value: C.c_single, optional: true },
  s3: { id: 's3', name: 'price', data: D.t_float, value: C.c_single, optional: true },
  s14: { id: 's14', name: 'tempRange', data: D.t_string, value: C.c_single, optional: true },
};

const LOG_PARTS = false;
const ENABLE_POLE_ZERO_SYNTHESIS = false;

let LTPartsPolesZeroes = function (
  item: Record<
    OpAmpPSTParameterName | 'spice' | 'cfb' | 'spiceModels' | 'polesZeroes',
    string | Record<string, unknown>
  >,
  logger: LoggingService,
): PolesZeroesDetails | undefined {
  let result: PolesZeroesDetails | undefined;
  if (ENABLE_POLE_ZERO_SYNTHESIS && (item.partNumber as string).substring(0, 2) === 'LT') {
    let gbw = parseFloat(item.gbw as string);
    if (LOG_PARTS) {
      logger.log(`Synthesizing polesZeroes for ${item.partNumber}`);
    }
    result = {
      gbw: gbw,
      domPole: 0.15 * gbw,
      p: [2.5 * gbw],
      z: [0.5 * gbw],
      rhpz: 1e14,
    };
  }
  return result;
};

export default class OpampService extends Service {
  @service('aditools-site') declare siteconfig: SiteService; //{ [key: string]: string };
  @service('model') declare modelService: ModelService;
  @service('opamp') declare opampService: OpampService;
  @service declare cdp: CdpService;
  @service declare logger: LoggingService;
  opAmps?: Promise<OpAmpSpec[] | null>;
  pstData?: PSTData | null;
  pstViewPromise?: Promise<PSTIndex | null>;
  pstView?: PSTIndex | null;
  pstMappingPromise?: Promise<PSTParameterMapping | null>;
  pstMapping?: PSTParameterMapping | null;
  // Load the opamps from cdp
  load(config?: Promise<PhotodiodeConfigJson | null>): void {
    if (config) {
      this.pstMappingPromise = this.cdp.getPstMapping<PSTParameterMapping>();
      this.opAmps = config.then((cfg) => {
        if (cfg === null) return cfg;
        this.pstViewPromise = this.cdp.getPstIndex<PSTIndex>(cfg.cdpsource.viewsource);
        let pst = this.cdp.getPstData<PSTData>(cfg.cdpsource.datasource);
        return pst.then(async (resolvedPst) => {
          this.pstData = resolvedPst;
          if (resolvedPst === null) return resolvedPst;
          return ParametricData.load<
            OpAmpPSTParameterName,
            OpAmpPSTParameterName | 'spice' | 'cfb' | 'spiceModels' | 'polesZeroes'
          >(
            this.siteconfig.AdiLocale,
            config,
            pst,
            PST_PARAMETERS,
            ['cfbParts:cfb', 'spiceModels', 'polesZeroes'],
            this.logger,
            undefined,
            LOG_PARTS,
          ).then((data) => {
            if (data === null) return data;
            return data
              .filter((item) => {
                // Remove currrent feedback parts without cfb data in the json
                return item.feedbackMethod !== 'Current' || item['cfb'];
              })
              .map((item) => {
                return {
                  partNumber: item.partNumber as string,
                  gbw: parseFloat(item.gbw as string),
                  slewRate: parseFloat(item.slewRate as string),
                  rOut: parseFloat(item.rOut as string),
                  supplyCurrent: parseFloat(item.supplyCurrent as string),
                  iNoise: parseFloat(item.iNoise as string),
                  vNoise: parseFloat(item.vNoise as string),
                  outHeadroomPlus: parseFloat(item.outHeadroomPlus as string),
                  outHeadroomMinus: parseFloat(item.outHeadroomMinus as string),
                  inHeadroomPlus: parseFloat(item.inHeadroomPlus as string),
                  inHeadroomMinus: parseFloat(item.inHeadroomMinus as string),
                  feedbackMethod: item.feedbackMethod as string,
                  architecture: item.architecture as string,
                  minSupplyVoltage: parseFloat(item.minSupplyVoltage as string),
                  maxSupplyVoltage: parseFloat(item.maxSupplyVoltage as string),
                  numberChannels: parseInt(item.numberChannels as string),
                  packageCode: item.packageCode as string,
                  minStableGain: parseFloat(item.minStableGain as string),
                  iBias: parseFloat(item.iBias as string),
                  vos: parseFloat(item.vos as string),
                  cCm: parseFloat(item.cCm as string),
                  cDiff: parseFloat(item.cDiff as string),
                  vintage: parseInt(item.vintage as string),
                  statusText: item.statusText as string,
                  deviceStatus: parseInt(item.deviceStatus as string),
                  price: parseFloat(item.price as string),
                  tempRange: item.tempRange as string,
                  spice: item.spice !== undefined && (item.spice as SpiceDetails).value === 'Yes',
                  cfb: item.cfb as CfbDetails,
                  spiceModels: item.spiceModels as SpiceModelDetails,
                  polesZeroes: item.polesZeroes
                    ? (item.polesZeroes as PolesZeroesDetails)
                    : LTPartsPolesZeroes(item, this.logger),
                  generic: item.generic as string,
                  cfint: parseFloat((item.partNumber == 'ADA4351-2' ? '3e-12' : '0') as string),
                };
              });
          });
        });
      });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    opamp: OpampService;
  }
}

// Pulled from old code in FilterWizard - probably not needed in Photodiode

// postprocess(mergedPart, part)
// {
//     if (mergedPart.partId.substring(0, 2) === "LT") {
//         let GBW = part['gbw'];
//         part['pole1'] = 0.15 * GBW;
//         part['pole2'] = 2.5 * GBW;
//         part['zero'] = 0.5 * GBW;
//         part['compPin'] = false;
//         part['specialOpAmp'] = true;
//     } else if ('special' in mergedPart) {
//         part['minStableGain'] = mergedPart['special'].minGain;
//         part['pole1'] = mergedPart['special'].pole1;
//         part['pole2'] = mergedPart['special'].pole2;
//         part['zero'] = mergedPart['special'].zero;
//         part['compPin'] = mergedPart['special'].compPin;
//         part['specialOpAmp'] = true;
//     } else {
//         part['specialOpAmp'] = false;
//     }
// }
